import { api } from "../../../lib/axios";

import { apiSlice } from "../../../features/api/apiSlice";
export const getLogbook = () => api.get("/logbook");
export const addLogbook = (data: any) => api.post("/logbook", data);
export const getLogbooks = () => api.get("/logbook/me");

export type TrainingCode = {
  id: number;
  code: string;
  description: string;
};

export const logbookApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMyLogbooks: builder.query<any[], void>({
      query: () => "/logbook/me",
      transformResponse: (res: any) =>
        res?.data?.data || res?.data || res || [], // auto normalize
    }),

    /**
     * The candidate's own result letter, already worded for their outcome and
     * with every placeholder filled. Rendered server-side from the diet's
     * settings so the page cannot word a result differently from the record.
     * 403 while a diet-assessed result is still awaiting board approval.
     */
    getMyLogbookResultLetter: builder.query<any, string>({
      query: (id) => `/logbook/${id}/result-letter`,
      transformResponse: (res: any) => res?.data ?? res,
    }),
    getLogbooks: builder.query<any[], void>({
      query: () => "/logbook",
      transformResponse: (res: any) =>
        res?.data?.data || res?.data || res || [], // auto normalize
    }),

    getWeekEntries: builder.query({
      query: (week: number) => `/logbook/entries/week/${week}`,
    }),
    // /api/logbook/019af9df-e75f-73ff-bc87-9a21cf3cc349/entries/week/1
    // getLogbookWeekEntries: builder.query({
    //   query: ({ logbook_id, week }: { logbook_id: string; week: number }) =>
    //     `/logbook/${encodeURIComponent(logbook_id)}/entries/week/${week}`,
    // }),
    getLogbookWeekEntries: builder.query<
      any,
      { logbook_id: string; week: number }
    >({
      query: ({ logbook_id, week }) =>
        `/logbook/${encodeURIComponent(logbook_id)}/entries/week/${week}`,
      transformResponse: (res: any) => res, // ✅ fetchBaseQuery already gives JSON
      providesTags: (_res, _err, arg) => [
        { type: "LogbookWeek", id: `LB:${arg.logbook_id}:W:${arg.week}` },
      ],
    }),

    // Analysis sheet — subjects (rows) × weeks (cols) coverage matrix.
    getLogbookAnalysisSheet: builder.query<any, { logbook_id: string }>({
      query: ({ logbook_id }) =>
        `/logbook/${encodeURIComponent(logbook_id)}/analysis-sheet`,
      transformResponse: (res: any) => res ?? {},
      providesTags: (_res, _err, arg) => [
        { type: "Logbook", id: `ANALYSIS:${arg.logbook_id}` },
      ],
    }),

    saveEntry: builder.mutation<any, any>({
      query: (body: any) => ({
        url: "/logbook/entries",
        method: "POST",
        body, // ✅ correct for fetchBaseQuery
      }),
      invalidatesTags: (_res, _err, body) => [
        { type: "LogbookWeek", id: `LB:${body.logbook_id}:W:${body.week}` },
        // Saving a day answers any review on it, which moves the week's flag
        // from the candidate's calendar to the supervisor's — so the calendar
        // has to be re-read, not just the week's entries.
        "LogbookWeeksStatus",
      ],
    }),

    searchSupervisor: builder.query({
      query: (term: string) => `/members/searchm/${encodeURIComponent(term)}`,
    }),

    requestSupervisor: builder.mutation({
      query: (body) => ({
        url: "/logbook/supervisor-request",
        method: "POST",
        body,
      }),
    }),

    cancelSupervisorRequest: builder.mutation<any, string>({
      query: (id) => ({
        url: `/logbook/supervisor-request/${encodeURIComponent(id)}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["SupervisorRequests"],
    }),

    getMySupervisorRequests: builder.query<any, { decision?: string }>({
      query: ({ decision } = {}) =>
        `/logbook/supervisor-requests/me?decision=${encodeURIComponent(
          decision || "pending",
        )}`,
      transformResponse: (res: any) => res, // ✅ fetchBaseQuery already JSON
      providesTags: ["LogbookSupervisorRequests"],
    }),

    getSupervisorRequests: builder.query<any[], void>({
      query: () => "/logbook/supervisor-request",
      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        return [];
      },
      providesTags: ["SupervisorRequests"],
    }),

    respondSupervisorRequest: builder.mutation<
      any,
      { id: string; status: "accepted" | "rejected" }
    >({
      query: ({ id, status }) => ({
        url: `/logbook/supervisor-request/${encodeURIComponent(id)}`,
        method: "POST",
        body: { status },
      }),
      invalidatesTags: ["SupervisorRequests", "Logbook"],
    }),

    getMySupervisorPendingStatus: builder.query<
      { has_pending: boolean; pending_request?: any },
      { logbook_id?: string } | void
    >({
      query: (arg) => {
        const logbook_id = (arg as any)?.logbook_id;
        const qs = logbook_id
          ? `?logbook_id=${encodeURIComponent(logbook_id)}`
          : "";
        return `/logbook/supervisor-request/pending-status${qs}`;
      },
      providesTags: ["SupervisorRequests"],
    }),
    getSupervisorAcceptedRequests: builder.query<any[], void>({
      query: () => "/logbook/supervisor-request/accepted",
      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        return [];
      },
      providesTags: ["SupervisorRequests"],
    }),

    /**
     * ✅ supervisor: fetch a single supervisee logbook details (optional but helpful)
     * You can remove this if you already have a logbook details endpoint.
     */
    getSupervisorLogbookDetails: builder.query<any, { logbook_id: string }>({
      query: ({ logbook_id }) => `/logbook/${encodeURIComponent(logbook_id)}`,
      transformResponse: (res: any) => {
        //console.log(res, "this is the response");
        return res?.data?.data ?? res?.data ?? res ?? {};
      },
      providesTags: (_res, _err, arg) => [
        { type: "Logbook", id: arg.logbook_id },
      ],
    }),

    /**
     * ✅ supervisor: fetch entries for a logbook + week (or a "pending" list)
     * CHANGE THIS endpoint to whatever you have.
     */
    getSupervisorLogbookWeekEntries: builder.query<
      any,
      { logbook_id: string; week: number }
    >({
      query: ({ logbook_id, week }) =>
        `/logbook/${encodeURIComponent(logbook_id)}/entries/week/${week}`,
      transformResponse: (res: any) =>
        res?.data?.data ?? res?.data ?? res ?? {},
      providesTags: (_res, _err, arg) => [
        { type: "LogbookWeek", id: `LB:${arg.logbook_id}:W:${arg.week}` },
      ],
    }),

    /**
     * ✅ supervisor: approve/reject a single entry (with optional comment)
     * CHANGE THIS endpoint to whatever you have.
     */
    approveDailyLog: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/logbook/entries/approve/${encodeURIComponent(id)}`,
        method: "POST",
      }),
      invalidatesTags: ["LogbookWeek", "Logbook", "LogbookWeeksStatus"],
    }),
    // Approve everything pending in one week, however few days are filled. The
    // per-day mutation above stays for whatever the candidate writes later.
    approveLogbookWeek: builder.mutation<any, { logbookId: string; week: number }>({
      query: ({ logbookId, week }) => ({
        url: `/logbook/${encodeURIComponent(logbookId)}/entries/approve-week`,
        method: "POST",
        body: { week },
      }),
      invalidatesTags: ["LogbookWeek", "Logbook", "LogbookWeeksStatus"],
    }),
    // Supervisor's review of one day. An empty body withdraws it.
    commentOnEntry: builder.mutation<any, { entryId: string; comment: string }>({
      query: ({ entryId, comment }) => ({
        url: `/logbook/entries/${encodeURIComponent(entryId)}/comment`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: ["LogbookWeek", "LogbookWeeksStatus"],
    }),

    // Candidate answers a review. This hands the entry back to the supervisor;
    // it does not close the review — only their approval does. Editing the
    // entry does the same thing, server-side.
    respondToEntryComment: builder.mutation<any, { entryId: string }>({
      query: ({ entryId }) => ({
        url: `/logbook/entries/${encodeURIComponent(entryId)}/comment/respond`,
        method: "POST",
      }),
      invalidatesTags: ["LogbookWeek", "LogbookWeeksStatus"],
    }),

    // Whole-logbook flags for the calendar. The week endpoint only ever returns
    // one week, so the other 51 squares would have nothing to shade with.
    getLogbookWeeksStatus: builder.query<any, { logbookId: string }>({
      query: ({ logbookId }) => `/logbook/${encodeURIComponent(logbookId)}/weeks-status`,
      providesTags: ["LogbookWeeksStatus"],
    }),

    sendLogbookForAssessment: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/logbook/send/for/assessement/${encodeURIComponent(id)}`,
        method: "GET",
      }),
    }),

    getSupervisorLogbooksGroupedByDiet: builder.query<any, void>({
      query: () => `/logbook/supervisor/logbooks/grouped-by-diet`,
    }),

    assessLogbook: builder.mutation<
      any,
      {
        logbookId: string;
        details: number;
        practicality: number;
        correctness: number;
        creativity: number;
        presentation: number;
        comment?: string;
        result: "pass" | "fail";
        repeat_type?: "full" | "half";
      }
    >({
      query: ({ logbookId, ...body }) => ({
        url: `/logbook/${logbookId}/assess`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "Logbook", id: arg.logbookId },
      ],
    }),

    startLogbook: builder.mutation<{ message: string; data: any }, void>({
      query: () => ({ url: '/logbook', method: 'POST', body: {} }),
      invalidatesTags: ['Logbook'],
    }),

    /**
     * Renumber a logbook that was opened at the stage the candidate had just
     * passed. The server recomputes the correct stage from their own passed
     * logbooks — there is nothing to send and nothing to choose.
     */
    rectifyLogbookStage: builder.mutation<{ message: string; data: any }, void>({
      query: () => ({ url: '/logbook/rectify-stage', method: 'POST', body: {} }),
      invalidatesTags: ['Logbook'],
    }),

    // Upload the logbook report (PDF, max 5 pages). FormData with `report`.
    uploadLogbookReport: builder.mutation<any, { logbookId: string; file: File }>({
      query: ({ logbookId, file }) => {
        const body = new FormData();
        body.append("report", file);
        return { url: `/logbook/${logbookId}/report`, method: "POST", body };
      },
      invalidatesTags: (_res, _err, arg) => [
        "Logbook",
        { type: "Logbook", id: arg.logbookId },
      ],
    }),

    // The candidate accepts the scheme's terms for one logbook. Acceptance is
    // per logbook (each stage is agreed to on its own), so the tag invalidation
    // has to name the logbook — refetching the list is what un-gates the page.
    acceptLogbookTerms: builder.mutation<any, { logbookId: string }>({
      query: ({ logbookId }) => ({
        url: `/logbook/${logbookId}/accept-terms`,
        method: "POST",
        body: { accepted: true },
      }),
      invalidatesTags: (_res, _err, arg) => [
        "Logbook",
        { type: "Logbook", id: arg.logbookId },
      ],
    }),

    /* ── Candidate ↔ supervisor thread on one logbook ──────────────────── */

    // Polled by the chat widget. The response carries the caller's role and the
    // unread count, so the widget never has to work out who it is talking to.
    getLogbookMessages: builder.query<any, { logbookId: string }>({
      query: ({ logbookId }) => `/logbook/${logbookId}/messages`,
      providesTags: (_r, _e, arg) => [{ type: "LogbookMessages" as const, id: arg.logbookId }],
    }),

    sendLogbookMessage: builder.mutation<any, { logbookId: string; body: string }>({
      query: ({ logbookId, body }) => ({
        url: `/logbook/${logbookId}/messages`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "LogbookMessages" as const, id: arg.logbookId }],
    }),

    // Called when the thread is actually open — not on poll, which would eat
    // the badge it exists to raise.
    markLogbookMessagesRead: builder.mutation<any, { logbookId: string }>({
      query: ({ logbookId }) => ({
        url: `/logbook/${logbookId}/messages/read`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "LogbookMessages" as const, id: arg.logbookId }],
    }),

    // Supervisor: revert a submitted logbook back to in_progress.
    unsubmitLogbook: builder.mutation<any, { logbookId: string }>({
      query: ({ logbookId }) => ({
        url: `/logbook/logbooks/${encodeURIComponent(logbookId)}/unsubmit`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, arg) => [
        "Logbook",
        { type: "Logbook", id: arg.logbookId },
      ],
    }),

    // Candidate: read assessment-fee payment status for a logbook.
    getAssessmentFeeStatus: builder.query<any, { logbookId: string }>({
      query: ({ logbookId }) =>
        `/logbook/${encodeURIComponent(logbookId)}/assessment-fee/status`,
      transformResponse: (res: any) => res?.data ?? res ?? {},
      providesTags: (_res, _err, arg) => [
        { type: "Logbook", id: arg.logbookId },
      ],
    }),

    // Candidate: start an online (OnePay) assessment-fee payment.
    // Returns OnePay merchant params to POST to the gateway.
    initiateAssessmentFee: builder.mutation<any, { logbookId: string }>({
      query: ({ logbookId }) => ({
        url: `/logbook/${encodeURIComponent(logbookId)}/assessment-fee/initiate`,
        method: "POST",
      }),
    }),

    // Admin: record an offline (bank) assessment payment. FormData: receipt?, bank_account_id?, reference?
    recordOfflineAssessmentFee: builder.mutation<
      any,
      { logbookId: string; body: FormData }
    >({
      query: ({ logbookId, body }) => ({
        url: `/admin/logbook/${encodeURIComponent(logbookId)}/assessment-fee/offline`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        "Logbook",
        { type: "Logbook", id: arg.logbookId },
      ],
    }),

    getTrainingCodes: builder.query<TrainingCode[], void>({
      query: () => "/training-codes",
      transformResponse: (res: any) => {
        //  console.log(res, "thesse are the resss&&&&&&&&&&&&&&&&&&&&&&&&&&");
        console.log(
          res,
          "this is hywhhhhhhhhhhhhhhhe forrr*************************************",
        );
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },
    }),
  }),
});

export const {
  useGetMyLogbooksQuery,
  useGetMyLogbookResultLetterQuery,
  useGetLogbooksQuery,
  useGetWeekEntriesQuery,
  useSaveEntryMutation,
  useSearchSupervisorQuery,
  useRequestSupervisorMutation,
  useCancelSupervisorRequestMutation,
  useGetLogbookWeekEntriesQuery,
  useGetLogbookAnalysisSheetQuery,
  useGetMySupervisorRequestsQuery,
  useGetSupervisorRequestsQuery,
  useRespondSupervisorRequestMutation,
  useGetMySupervisorPendingStatusQuery,
  useGetSupervisorAcceptedRequestsQuery,
  useGetSupervisorLogbookDetailsQuery,
  useGetSupervisorLogbookWeekEntriesQuery,
  useApproveDailyLogMutation,
  useApproveLogbookWeekMutation,
  useCommentOnEntryMutation,
  useRespondToEntryCommentMutation,
  useGetLogbookWeeksStatusQuery,
  useSendLogbookForAssessmentMutation,
  useGetSupervisorLogbooksGroupedByDietQuery,
  useAssessLogbookMutation,
  useGetTrainingCodesQuery,
  useStartLogbookMutation,
  useRectifyLogbookStageMutation,
  useUploadLogbookReportMutation,
  useAcceptLogbookTermsMutation,
  useGetLogbookMessagesQuery,
  useSendLogbookMessageMutation,
  useMarkLogbookMessagesReadMutation,
  useUnsubmitLogbookMutation,
  useGetAssessmentFeeStatusQuery,
  useInitiateAssessmentFeeMutation,
  useRecordOfflineAssessmentFeeMutation,
} = logbookApi;
